import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { conversationTemplates } from "@samklang/content";
import type { AnnanRole, Perspective } from "@samklang/schemas";
import { ArrowRight } from "lucide-react";
import { Button, Card, Input } from "@samklang/ui";
import { useKartlaggningStore } from "@/stores/kartlaggning-store";
import { ANNAN_ROLES, PERSPECTIVES, perspectiveLabel, perspectiveShortLabel } from "./lib";

const perspectiveOptions: readonly { value: Perspective; label: string }[] = PERSPECTIVES.map((perspective) => ({
  value: perspective,
  label: perspectiveLabel(perspective),
}));

export function KartlaggningNewScreen() {
  const navigate = useNavigate();
  const createKartlaggning = useKartlaggningStore((store) => store.createKartlaggning);

  const kartlaggningTemplates = useMemo(
    () => conversationTemplates.filter((template) => template.perspectives && template.perspectives.length > 0),
    [],
  );
  const [templateId, setTemplateId] = useState(kartlaggningTemplates[0]?.id ?? "");
  const template = kartlaggningTemplates.find((candidate) => candidate.id === templateId);

  const [selected, setSelected] = useState<readonly Perspective[]>(template?.perspectives ?? []);
  const [annanRole, setAnnanRole] = useState<AnnanRole>("specialpedagog");
  const [participantReference, setParticipantReference] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [conductedWith, setConductedWith] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const toggle = (perspective: Perspective) => {
    setSelected((current) => (current.includes(perspective) ? current.filter((item) => item !== perspective) : [...current, perspective]));
  };

  const effectiveTemplate = kartlaggningTemplates.find((candidate) => candidate.id === templateId) ?? template;
  const invalid = !effectiveTemplate || selected.length === 0 || !participantReference.trim() || !createdBy.trim();

  const submit = () => {
    setSubmitted(true);
    if (invalid) return;
    const trimmedConductedWith = conductedWith.trim();
    const chosenRole = selected.includes("annan") ? annanRole : undefined;
    const id = createKartlaggning({
      templateId: effectiveTemplate.id,
      participantReference: participantReference.trim(),
      createdBy: createdBy.trim(),
      perspectives: selected,
      ...(trimmedConductedWith ? { conductedWith: trimmedConductedWith } : {}),
      ...(chosenRole ? { annanRole: chosenRole } : {}),
    });
    void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId: id } });
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">Ny kartläggning</p>
      <h1 className="font-display text-4xl font-semibold tracking-tight">Starta en kartläggning</h1>
      <p className="mt-3 text-foreground-muted">Välj mall, vilka perspektiv som ska samlas in och vem som ansvarar.</p>

      <div className="mt-7 grid gap-5">
        <Card>
          <h2 className="mb-3 font-display text-lg font-semibold">Välj mall</h2>
          {kartlaggningTemplates.length === 0 ? (
            <p className="text-sm text-foreground-muted">Inga kartläggningsmallar finns tillgängliga.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {kartlaggningTemplates.map((candidate) => (
                <label key={candidate.id} className="relative">
                  <input
                    type="radio"
                    name="template"
                    value={candidate.id}
                    checked={candidate.id === templateId}
                    onChange={() => {
                      setTemplateId(candidate.id);
                      setSelected(candidate.perspectives ?? [candidate.perspectives?.[0] ?? "elev"]);
                    }}
                    className="peer sr-only"
                  />
                  <span className="flex min-h-20 flex-col rounded-2xl border border-border bg-surface px-4 py-3 peer-checked:border-primary peer-checked:bg-primary-soft peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                    <strong>{candidate.title}</strong>
                    <small className="text-foreground-muted">{candidate.questions.length} frågor · {candidate.estimatedMinutes} min</small>
                  </span>
                </label>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 font-display text-lg font-semibold">Vilka perspektiv vill du samla in?</h2>
          <div className="grid gap-3 min-[500px]:grid-cols-2">
            {perspectiveOptions.map((option) => (
              <label key={option.value} className="relative">
                <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} className="peer sr-only" />
                <span className="flex min-h-14 items-center rounded-2xl border border-border bg-surface px-4 font-semibold peer-checked:border-primary peer-checked:bg-primary-soft peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
          {selected.includes("annan") ? (
            <label className="mt-4 grid gap-1 font-semibold">
              Annan roll
              <select
                value={annanRole}
                onChange={(event) => setAnnanRole(event.target.value as AnnanRole)}
                className="min-h-11 rounded-xl border border-border bg-surface-raised px-3"
              >
                {ANNAN_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {perspectiveLabel("annan", role)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {submitted && selected.length === 0 ? (
            <p role="alert" className="mt-2 text-sm text-danger">Välj minst ett perspektiv.</p>
          ) : null}
        </Card>

        <Card>
          <h2 className="mb-4 font-display text-lg font-semibold">Deltagare och ansvar</h2>
          <div className="grid gap-4">
            <label htmlFor="kartlaggning-participant" className="grid gap-1 font-semibold">
              Lokalt deltagar-id
              <span className="text-sm font-normal text-foreground-muted">Verksamhetens kod (inte namn eller personnummer). Håller historik och uppföljning isär.</span>
              <Input id="kartlaggning-participant" value={participantReference} onChange={(event) => setParticipantReference(event.target.value)} maxLength={80} placeholder="t.ex. elev-124" />
            </label>
            <label htmlFor="kartlaggning-created-by" className="grid gap-1 font-semibold">
              Professionell (ansvarig)
              <span className="text-sm font-normal text-foreground-muted">Vem genomför kartläggningen.</span>
              <Input id="kartlaggning-created-by" value={createdBy} onChange={(event) => setCreatedBy(event.target.value)} maxLength={80} placeholder="t.ex. Specialpedagog M. Lind" />
            </label>
            <label htmlFor="kartlaggning-conducted-with" className="grid gap-1 font-semibold">
              Genomfört tillsammans med (standard)
              <span className="text-sm font-normal text-foreground-muted">Används som förval vid varje samtal, kan ändras per perspektiv.</span>
              <Input id="kartlaggning-conducted-with" value={conductedWith} onChange={(event) => setConductedWith(event.target.value)} maxLength={80} placeholder="t.ex. Kurator A. Berg" />
            </label>
          </div>
          {submitted && (!participantReference.trim() || !createdBy.trim()) ? (
            <p role="alert" className="mt-2 text-sm text-danger">Fyll i deltagar-id och ansvarig.</p>
          ) : null}
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-foreground-muted">
            {selected.length > 0 ? `${selected.map((perspective) => perspectiveShortLabel(perspective, perspective === "annan" ? annanRole : undefined)).join(", ")} kommer att samlas in.` : "Inget perspektiv valt ännu."}
          </p>
          <Button fullWidth className="sm:w-auto min-w-52" onClick={submit} disabled={!effectiveTemplate}>
            Skapa kartläggning <ArrowRight size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
}
